// src/components/DynamicJobForm.js

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Paper,
  Chip,
  Stack,
  LinearProgress,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CloseIcon from "@mui/icons-material/Close";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import SignatureCanvas from "react-signature-canvas";

const DynamicJobForm = ({ fields = [], onFormDataChange, onComplete, onClose, jobId, initialData = {} }) => {
  const [formData, setFormData] = useState(initialData);
  const [currentStep, setCurrentStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showAllQuestions, setShowAllQuestions] = useState(false);

  // Barcode scanner
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scanningFieldIdRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  const hasInitialized = useRef(false);
  const saveTimeoutRef = useRef(null);
  const signaturesRef = useRef({});

  useEffect(() => {
    if (!hasInitialized.current && initialData && Object.keys(initialData).length > 0) {
      setFormData(initialData);
      hasInitialized.current = true;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Signature canvas responsive fix ─────────────────────────────────────────
  // The SignatureCanvas component uses a fixed internal canvas resolution. When CSS
  // stretches it to 100% width, touch coordinates are offset from actual strokes.
  // This effect resizes the internal canvas dimensions to match the rendered size.
  useEffect(() => {
    const timeout = setTimeout(() => {
      Object.values(signaturesRef.current).forEach((sigCanvas) => {
        if (!sigCanvas) return;
        const canvas = sigCanvas.getCanvas();
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const w = Math.round(rect.width);
        const h = Math.round(rect.height);
        if (w > 0 && (canvas.width !== w || canvas.height !== h)) {
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, w, h);
        }
      });
    }, 80);
    return () => clearTimeout(timeout);
  }, [currentStep, showAllQuestions]);

  const debouncedSave = useCallback((data) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      if (onFormDataChange) onFormDataChange(data);
    }, 500);
  }, [onFormDataChange]);

  useEffect(() => () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); }, []);

  const handleChange = (fieldId, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [fieldId]: value };
      debouncedSave(newData);
      return newData;
    });
  };

  // ── Barcode Scanner ──────────────────────────────────────────────────────────

  const closeScanner = useCallback(() => {
    if (scanIntervalRef.current) { clearInterval(scanIntervalRef.current); scanIntervalRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setScannerOpen(false);
    setScanning(false);
  }, []);

  const openScanner = (fieldId) => {
    scanningFieldIdRef.current = fieldId;
    setScanError(null);
    setScanning(false);
    setScannerOpen(true);
  };

  useEffect(() => {
    if (!scannerOpen) return;
    let active = true;

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
        });
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        setTimeout(() => {
          if (videoRef.current && active) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); }
        }, 150);

        if (!("BarcodeDetector" in window)) {
          setScanError("Barcode detection is not supported on this browser. Please type the value manually.");
          return;
        }

        const detector = new window.BarcodeDetector({
          formats: ["qr_code", "code_128", "code_39", "code_93", "ean_13", "ean_8", "upc_a", "upc_e", "data_matrix", "itf"],
        });
        setScanning(true);

        scanIntervalRef.current = setInterval(async () => {
          if (!videoRef.current || videoRef.current.readyState < 2 || !active) return;
          try {
            const barcodes = await detector.detect(videoRef.current);
            if (barcodes.length > 0 && active) {
              handleChange(scanningFieldIdRef.current, barcodes[0].rawValue);
              closeScanner();
            }
          } catch (_) { /* keep scanning */ }
        }, 250);
      } catch (err) {
        if (active) setScanError(`Could not access camera: ${err.message}`);
      }
    };

    startCamera();
    return () => { active = false; closeScanner(); };
  }, [scannerOpen, closeScanner]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Photo Upload ─────────────────────────────────────────────────────────────

  const handlePhotoUpload = async (fieldId, event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setUploadError("Please select an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { setUploadError("Image too large. Maximum size is 10MB"); return; }
    setUploading(true);
    setUploadError(null);
    try {
      const storageRef = ref(storage, `jobs/${jobId}/${fieldId}_${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      handleChange(fieldId, [...(formData[fieldId] || []), url]);
    } catch (error) {
      setUploadError(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = (fieldId, photoIndex) => {
    handleChange(fieldId, (formData[fieldId] || []).filter((_, i) => i !== photoIndex));
  };

  // ── Signature ────────────────────────────────────────────────────────────────

  const handleSaveSignature = (fieldId) => {
    const sigCanvas = signaturesRef.current[fieldId];
    if (sigCanvas && !sigCanvas.isEmpty()) handleChange(fieldId, sigCanvas.toDataURL());
  };

  const handleClearSignature = (fieldId) => {
    signaturesRef.current[fieldId]?.clear();
    handleChange(fieldId, null);
  };

  // ── Conditional Logic ────────────────────────────────────────────────────────

  const shouldShowField = (field) => {
    if (!field.conditionalLogic?.enabled) return true;
    const { showIf } = field.conditionalLogic;
    if (!showIf) return true;
    const dep = formData[showIf.fieldId];
    if (dep === undefined || dep === null) return false;
    switch (showIf.operator) {
      case "equals": return dep === showIf.value;
      case "notEquals": return dep !== showIf.value;
      case "contains": return String(dep).includes(showIf.value);
      default: return true;
    }
  };

  const visibleFields = fields.filter(shouldShowField);
  const safeCurrentStep = Math.min(currentStep, Math.max(0, visibleFields.length - 1));
  const currentField = visibleFields[safeCurrentStep];
  const isRequired = currentField?.required && currentField?.type !== "section";
  const isLastQuestion = safeCurrentStep >= visibleFields.length - 1;

  const isCurrentFieldAnswered = () => {
    if (!currentField) return true;
    const value = formData[currentField.id];
    const t = (currentField.type || "").toLowerCase();
    if (["checkbox", "boolean", "yes/no", "yesno"].includes(t)) return value === true || value === false;
    if (t === "photo") return Array.isArray(value) && value.length > 0;
    if (t === "signature") return value?.startsWith?.("data:");
    if (["section", "section header", "sectionheader"].includes(t)) return true;
    return value !== "" && value !== null && value !== undefined;
  };

  const goToNext = () => { if (safeCurrentStep < visibleFields.length - 1) setCurrentStep(safeCurrentStep + 1); };
  const goToPrevious = () => { if (safeCurrentStep > 0) setCurrentStep(safeCurrentStep - 1); };

  const handleFinish = async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    setIsCompleting(true);
    if (onFormDataChange) onFormDataChange(formData);
    if (onComplete) {
      try { await onComplete(formData); }
      catch (error) { console.error("Error completing job:", error); setIsCompleting(false); }
    }
  };

  // ── Field Renderer ───────────────────────────────────────────────────────────

  const textSx = {
    mb: 2,
    "& .MuiInputBase-root": { fontSize: "1.1rem", color: "#ffffff" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "rgba(148,163,184,0.3)" },
      "&:hover fieldset": { borderColor: "rgba(148,163,184,0.5)" },
    },
    "& .MuiInputBase-input::placeholder": { color: "#64748b", opacity: 1 },
  };

  const renderField = (field) => {
    if (!field) return null;
    const value = formData[field.id];
    const t = (field.type || "").toLowerCase();

    switch (t) {
      case "text":
      case "short text":
      case "shorttext":
        return <TextField fullWidth placeholder="Type your answer..." required={field.required} value={value || ""} onChange={(e) => handleChange(field.id, e.target.value)} variant="outlined" sx={textSx} />;

      case "textarea":
      case "long text":
      case "longtext":
        return <TextField fullWidth placeholder="Type your answer..." required={field.required} value={value || ""} onChange={(e) => handleChange(field.id, e.target.value)} variant="outlined" multiline rows={4} sx={textSx} />;

      case "number":
        return <TextField fullWidth placeholder="Enter a number..." required={field.required} type="number" value={value || ""} onChange={(e) => handleChange(field.id, e.target.value)} variant="outlined" sx={textSx} />;

      case "barcode":
      case "serial":
        return (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                fullWidth
                placeholder="Scan or type serial / barcode..."
                required={field.required}
                value={value || ""}
                onChange={(e) => handleChange(field.id, e.target.value)}
                variant="outlined"
                sx={textSx}
                InputProps={{
                  endAdornment: value ? (
                    <IconButton size="small" onClick={() => handleChange(field.id, "")} sx={{ color: "#94a3b8" }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : null,
                }}
              />
              <Button
                variant="outlined"
                onClick={() => openScanner(field.id)}
                title="Scan QR / Barcode"
                sx={{ minWidth: 56, height: 56, borderColor: "rgba(148,163,184,0.3)", color: "#60a5fa", flexShrink: 0, "&:hover": { borderColor: "#60a5fa", bgcolor: "rgba(96,165,250,0.1)" } }}
              >
                <QrCodeScannerIcon />
              </Button>
            </Stack>
            {value && <Typography variant="body2" sx={{ mt: 0.5, color: "#10b981", fontWeight: 600 }}>✓ {value}</Typography>}
          </Box>
        );

      case "select":
      case "dropdown":
        return (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: "#94a3b8" }}>{field.label}</InputLabel>
            <Select value={value || ""} label={field.label} onChange={(e) => handleChange(field.id, e.target.value)} sx={{ fontSize: "1.1rem", color: "#ffffff", "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(148,163,184,0.3)" } }}>
              {(field.options || []).map((opt, i) => <MenuItem key={i} value={opt}>{opt}</MenuItem>)}
            </Select>
          </FormControl>
        );

      case "checkbox":
      case "boolean":
      case "yes/no":
      case "yesno":
        return (
          <Box sx={{ mb: 2 }}>
            <Stack spacing={2}>
              <Button fullWidth variant={value === true ? "contained" : "outlined"} onClick={() => handleChange(field.id, true)} sx={{ py: 2.5, fontSize: "1.1rem", fontWeight: 600, ...(value === true && { background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }) }}>✓ Yes</Button>
              <Button fullWidth variant={value === false ? "contained" : "outlined"} onClick={() => handleChange(field.id, false)} sx={{ py: 2.5, fontSize: "1.1rem", fontWeight: 600, ...(value === false && { background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" }) }}>✗ No</Button>
            </Stack>
          </Box>
        );

      case "photo": {
        const photos = Array.isArray(value) ? value : [];
        return (
          <Box sx={{ mb: 2 }}>
            {uploadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>{uploadError}</Alert>}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Button fullWidth variant="outlined" component="label" disabled={uploading} startIcon={uploading ? <CircularProgress size={20} /> : <PhotoCameraIcon />} sx={{ py: 2 }}>
                {uploading ? "Uploading..." : "Take Photo"}
                <input type="file" hidden accept="image/*" capture="environment" onChange={(e) => handlePhotoUpload(field.id, e)} />
              </Button>
              <Button fullWidth variant="outlined" component="label" disabled={uploading} startIcon={<PhotoLibraryIcon />} sx={{ py: 2 }}>
                Gallery
                <input type="file" hidden accept="image/*" onChange={(e) => handlePhotoUpload(field.id, e)} />
              </Button>
            </Stack>
            {photos.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {photos.map((url, i) => (
                  <Paper key={i} sx={{ width: "100%", maxWidth: 200, position: "relative", borderRadius: 2, overflow: "hidden" }}>
                    <img src={url} alt={`Photo ${i + 1}`} style={{ width: "100%", height: 150, objectFit: "cover" }} />
                    <IconButton onClick={() => handleDeletePhoto(field.id, i)} size="small" sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(0,0,0,0.6)", color: "white", "&:hover": { bgcolor: "rgba(220,38,38,0.9)" } }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                ))}
              </Box>
            )}
            {photos.length > 0 && <Typography variant="body2" sx={{ mt: 2, fontWeight: 600, color: "#10b981" }}>✓ {photos.length} photo{photos.length !== 1 ? "s" : ""} uploaded</Typography>}
          </Box>
        );
      }

      case "signature": {
        const isSigned = value?.startsWith?.("data:");
        return (
          <Box sx={{ mb: 2 }}>
            {isSigned ? (
              <Box sx={{ textAlign: "center" }}>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(16,185,129,0.1)", border: "2px solid rgba(16,185,129,0.3)", mb: 2 }}>
                  <Typography variant="body2" color="success.main" sx={{ mb: 1, fontWeight: 600 }}>✓ Signature Captured</Typography>
                  <img src={value} alt="Signature" style={{ maxWidth: "100%", height: "auto", maxHeight: 120, backgroundColor: "white", borderRadius: 8 }} />
                </Paper>
                <Button variant="outlined" color="warning" onClick={() => handleClearSignature(field.id)}>Clear & Re-sign</Button>
              </Box>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1, textAlign: "center", color: "#94a3b8" }}>Sign below, then tap "Save Signature"</Typography>
                {/* touchAction: none prevents the page scrolling while signing */}
                <Paper sx={{ border: "2px solid rgba(100,100,100,0.3)", borderRadius: 2, overflow: "hidden", mb: 2, bgcolor: "#ffffff", touchAction: "none" }}>
                  {/*
                    No fixed `width` in canvasProps — the useEffect at the top of this
                    component resizes the internal canvas dimensions to match the actual
                    rendered width (80ms after step change), fixing the touch offset issue.
                  */}
                  <SignatureCanvas
                    ref={(ref) => { if (ref) signaturesRef.current[field.id] = ref; }}
                    penColor="black"
                    backgroundColor="white"
                    canvasProps={{ style: { display: "block", width: "100%", height: "200px" } }}
                  />
                </Paper>
                <Stack direction="row" spacing={2}>
                  <Button fullWidth variant="outlined" onClick={() => handleClearSignature(field.id)}>Clear</Button>
                  <Button fullWidth variant="contained" onClick={() => handleSaveSignature(field.id)} sx={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>✓ Save Signature</Button>
                </Stack>
              </>
            )}
          </Box>
        );
      }

      case "section":
      case "section header":
      case "sectionheader":
        return (
          <Box sx={{ mb: 2, textAlign: "center", py: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#60a5fa", mb: 2 }}>📋 {field.label}</Typography>
            <Typography variant="body1" sx={{ color: "#94a3b8" }}>Tap "Next" to continue</Typography>
          </Box>
        );

      case "date":
        return <TextField fullWidth type="date" required={field.required} value={value || ""} onChange={(e) => handleChange(field.id, e.target.value)} variant="outlined" InputLabelProps={{ shrink: true }} sx={{ mb: 2, "& .MuiInputBase-root": { color: "#ffffff" }, "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(148,163,184,0.3)" } } }} />;

      case "time":
        return <TextField fullWidth type="time" required={field.required} value={value || ""} onChange={(e) => handleChange(field.id, e.target.value)} variant="outlined" InputLabelProps={{ shrink: true }} sx={{ mb: 2, "& .MuiInputBase-root": { color: "#ffffff" }, "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(148,163,184,0.3)" } } }} />;

      case "radio":
      case "radio buttons":
      case "radiobuttons":
        return (
          <Box sx={{ mb: 2 }}>
            <Stack spacing={1.5}>
              {(field.options || []).map((opt, i) => (
                <Button key={i} fullWidth variant={value === opt ? "contained" : "outlined"} onClick={() => handleChange(field.id, opt)} sx={{ py: 2, fontSize: "1rem", fontWeight: 600, justifyContent: "flex-start", textTransform: "none", color: "#ffffff", borderColor: "rgba(148,163,184,0.3)", ...(value === opt && { background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)" }) }}>
                  {value === opt ? "✓ " : ""}{opt}
                </Button>
              ))}
            </Stack>
          </Box>
        );

      default:
        return <TextField fullWidth placeholder="Type your answer..." value={value || ""} onChange={(e) => handleChange(field.id, e.target.value)} variant="outlined" sx={textSx} />;
    }
  };

  // ── Overview helpers ─────────────────────────────────────────────────────────

  const getDisplayValue = (field) => {
    const value = formData[field.id];
    const t = (field.type || "").toLowerCase();
    if (value === null || value === undefined || value === "") return null;
    if (["checkbox", "boolean", "yes/no", "yesno"].includes(t)) return value === true ? "Yes" : value === false ? "No" : null;
    if (t === "photo") return Array.isArray(value) && value.length > 0 ? `${value.length} photo${value.length !== 1 ? "s" : ""}` : null;
    if (t === "signature") return value?.startsWith?.("data:") ? "Signed" : null;
    if (["section", "section header", "sectionheader"].includes(t)) return "—";
    if (["barcode", "serial"].includes(t)) return typeof value === "string" ? value : null;
    if (typeof value === "string" && value.length > 50) return value.substring(0, 50) + "...";
    return String(value);
  };

  const answeredCount = visibleFields.filter((f) => {
    const t = (f.type || "").toLowerCase();
    if (["section", "section header", "sectionheader"].includes(t)) return true;
    return getDisplayValue(f) !== null;
  }).length;

  // ── Guards ───────────────────────────────────────────────────────────────────

  if (fields.length === 0) return <Box sx={{ textAlign: "center", py: 4 }}><Typography color="text.secondary">📋 No form fields configured</Typography></Box>;
  if (visibleFields.length === 0) return <Box sx={{ textAlign: "center", py: 4 }}><Typography color="text.secondary">No questions to display</Typography></Box>;

  // ── View All Questions ───────────────────────────────────────────────────────

  if (showAllQuestions) {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#ffffff" }}>All Questions</Typography>
          <Chip label={`${answeredCount}/${visibleFields.length} answered`} color={answeredCount === visibleFields.length ? "success" : "default"} size="small" />
        </Box>
        <Stack spacing={1.5}>
          {visibleFields.map((field, index) => {
            const displayValue = getDisplayValue(field);
            const isAnswered = displayValue !== null;
            const t = (field.type || "").toLowerCase();
            const isSection = ["section", "section header", "sectionheader"].includes(t);
            return (
              <Paper key={field.id} onClick={() => { setCurrentStep(index); setShowAllQuestions(false); }} sx={{ p: 2, borderRadius: 2, cursor: "pointer", border: isAnswered ? "1px solid rgba(16,185,129,0.3)" : "1px solid rgba(148,163,184,0.2)", bgcolor: isSection ? "rgba(59,130,246,0.1)" : isAnswered ? "rgba(16,185,129,0.05)" : "transparent", "&:hover": { bgcolor: "rgba(148,163,184,0.1)" }, transition: "all 0.2s" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: isSection ? "primary.main" : isAnswered ? "success.main" : "rgba(148,163,184,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {isAnswered && !isSection ? <CheckCircleIcon sx={{ fontSize: 16, color: "white" }} /> : <Typography variant="caption" sx={{ color: "white", fontWeight: 600 }}>{index + 1}</Typography>}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: isSection ? 700 : 500, color: isSection ? "#60a5fa" : "#ffffff", mb: 0.5 }}>
                      {field.label}{field.required && !isSection && <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>}
                    </Typography>
                    {displayValue && !isSection && <Typography variant="caption" sx={{ color: "#10b981", display: "block" }}>✓ {displayValue}</Typography>}
                    {!displayValue && !isSection && <Typography variant="caption" sx={{ color: "#64748b" }}>Tap to answer</Typography>}
                  </Box>
                  <ArrowForwardIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                </Box>
              </Paper>
            );
          })}
        </Stack>
        <Button fullWidth variant="outlined" onClick={() => setShowAllQuestions(false)} startIcon={<ArrowBackIcon />} sx={{ mt: 3, py: 1.5, textTransform: "none" }}>Back to Current Question</Button>
        {answeredCount === visibleFields.length && (
          <Button fullWidth variant="contained" size="large" onClick={handleFinish} disabled={isCompleting} endIcon={isCompleting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />} sx={{ mt: 2, py: 2, fontSize: "1.1rem", fontWeight: 700, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", textTransform: "none" }}>
            {isCompleting ? "Completing Job..." : "✓ Complete Job"}
          </Button>
        )}
      </Box>
    );
  }

  // ── Main Step View ───────────────────────────────────────────────────────────

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>Question {safeCurrentStep + 1} of {visibleFields.length}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#60a5fa" }}>{Math.round(((safeCurrentStep + 1) / visibleFields.length) * 100)}% Complete</Typography>
        </Box>
        <LinearProgress variant="determinate" value={((safeCurrentStep + 1) / visibleFields.length) * 100} sx={{ height: 8, borderRadius: 4, bgcolor: "rgba(148,163,184,0.2)", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)" } }} />
      </Box>

      <Box sx={{ minHeight: 280, mb: 3 }}>
        {currentField ? (
          <>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, fontSize: "1.2rem", color: "#ffffff" }}>
              {currentField.label}{isRequired && <span style={{ color: "#ef4444" }}> *</span>}
            </Typography>
            {renderField(currentField)}
          </>
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}><Typography color="text.secondary">Loading question...</Typography></Box>
        )}
      </Box>

      <Stack spacing={2}>
        {!isLastQuestion ? (
          <Button fullWidth variant="contained" size="large" onClick={goToNext} disabled={isRequired && !isCurrentFieldAnswered()} endIcon={<ArrowForwardIcon />} sx={{ py: 2, fontSize: "1rem", fontWeight: 700, background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)", textTransform: "none", "&:disabled": { background: "rgba(148,163,184,0.3)" } }}>
            Next Question
          </Button>
        ) : (
          <Button fullWidth variant="contained" size="large" onClick={handleFinish} disabled={(isRequired && !isCurrentFieldAnswered()) || isCompleting} endIcon={isCompleting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />} sx={{ py: 2.5, fontSize: "1.1rem", fontWeight: 700, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", textTransform: "none", "&:disabled": { background: isCompleting ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" : "rgba(148,163,184,0.3)", color: isCompleting ? "white" : undefined } }}>
            {isCompleting ? "Completing Job..." : "✓ Complete Job"}
          </Button>
        )}
        {safeCurrentStep > 0 && (
          <Button fullWidth variant="outlined" size="large" onClick={goToPrevious} startIcon={<ArrowBackIcon />} sx={{ py: 1.5, fontSize: "1rem", textTransform: "none" }}>Previous</Button>
        )}
      </Stack>

      {!isRequired && !isLastQuestion && (
        <Button fullWidth variant="text" onClick={goToNext} sx={{ mt: 1, textTransform: "none", color: "#94a3b8" }}>Skip this question</Button>
      )}

      <Button fullWidth variant="text" onClick={() => setShowAllQuestions(true)} sx={{ mt: 2, py: 1.5, textTransform: "none", color: "#94a3b8", borderTop: "1px solid rgba(148,163,184,0.2)", borderRadius: 0 }}>
        📋 View All Questions ({answeredCount}/{visibleFields.length} answered)
      </Button>

      {/* ── Barcode Scanner Dialog ──────────────────────────────────────────── */}
      <Dialog open={scannerOpen} onClose={closeScanner} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: "#0f172a", border: "1px solid rgba(148,163,184,0.2)" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", color: "#ffffff" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <QrCodeScannerIcon sx={{ color: "#60a5fa" }} />
            Scan Barcode / QR Code
          </Box>
          <IconButton onClick={closeScanner} sx={{ color: "#94a3b8" }}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent>
          {scanError ? (
            <Alert severity="warning">{scanError}</Alert>
          ) : (
            <>
              <Box sx={{ position: "relative", width: "100%", borderRadius: 2, overflow: "hidden", bgcolor: "#000", mb: 2 }}>
                <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", display: "block", maxHeight: 320, objectFit: "cover" }} />
                {/* Viewfinder overlay */}
                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                  <Box sx={{ width: 200, height: 200, border: "2px solid rgba(96,165,250,0.9)", borderRadius: 2, boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)" }} />
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
                {scanning ? <CircularProgress size={16} sx={{ color: "#60a5fa" }} /> : null}
                <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                  {scanning ? "Scanning — point camera at barcode or QR code" : "Starting camera..."}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeScanner} variant="outlined" fullWidth sx={{ textTransform: "none" }}>Cancel — Enter Manually</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DynamicJobForm;